import type { TransactionTrendsResponse } from '@/store/transactionsApi';

type TrendRecords = TransactionTrendsResponse['Records'];

/** Whether any series reports activity in the bucket at `index`. */
function hasActivity(records: TrendRecords, index: number): boolean {
  return records.series.some((series) => {
    const value = series.data[index];
    return typeof value === 'number' && value !== 0;
  });
}

/**
 * Narrows a trend response to the span that actually holds transactions.
 *
 * "All Time" asks the API for everything from a floor far older than any real
 * transaction, and the API answers with a bucket per month in between. That
 * leaves the chart plotting decades of empty months before the first
 * transaction, squeezing the real data into the right-hand edge (EUD-901).
 *
 * Only the empty ends are dropped. A quiet month between two active ones is
 * part of the account's history and stays, so the timeline keeps its shape.
 */
export function trimEmptyTrendEdges(
  data: TransactionTrendsResponse,
): TransactionTrendsResponse {
  const records = data.Records;
  const length = records.categories.length;

  let first = 0;
  while (first < length && !hasActivity(records, first)) {
    first += 1;
  }

  // Every bucket is empty: there is no span to narrow to, so leave the response
  // alone and let the consumer render its own empty state.
  if (first === length) {
    return data;
  }

  let last = length - 1;
  while (last > first && !hasActivity(records, last)) {
    last -= 1;
  }

  if (first === 0 && last === length - 1) {
    return data;
  }

  return {
    ...data,
    Records: {
      ...records,
      categories: records.categories.slice(first, last + 1),
      series: records.series.map((series) => ({
        ...series,
        data: series.data.slice(first, last + 1),
      })),
    },
  };
}

/**
 * First day of the earliest month holding a transaction, in the app's
 * MM/DD/YYYY form, or null when the response has no transactions at all.
 *
 * Trimming fixes what the chart plots, but the selected range still starts at
 * the All Time floor — so reopening the date picker drops the user in 1900
 * (EUD-901). This is the date the range should settle on instead.
 */
export function firstActiveMonthStart(
  data: TransactionTrendsResponse,
): string | null {
  const records = data.Records;
  const index = records.categories.findIndex((_category, position) =>
    hasActivity(records, position),
  );
  if (index === -1) return null;

  // Categories arrive as "M/YYYY".
  const [month, year] = records.categories[index].name.split('/');
  if (!month || !year) return null;

  return `${month.padStart(2, '0')}/01/${year}`;
}
